/**
 * Confirmation Window Controller
 * 
 * This controller handles the confirmation dialog that is shown when the user needs to confirm an action.
 * 
 * It listens for the 'show-confirmation' event and displays the confirmation dialog.
 * 
 * Example usage:
 * ```javascript
    window.dispatchEvent(new CustomEvent('show-confirmation', {
      detail: {
        title: '...',
        message: '....',
        confirmLabel: '...',
        cancelLabel: '...',
        onConfirm: () => {
          // ...
        },
        onCancel: () => {
          // ...
        }
      }
    }))
 * ```
 */

import { Controller } from '@hotwired/stimulus'

export default class extends Controller {

  static targets = ['dialog', 'message', 'confirmButton', 'cancelButton', 'confirmLabel', 'cancelLabel']
  
  dialog = null

  connect() {
    this.dialog = this.element.querySelector('dialog')
    //this.dialog = this.dialogTarget
    this.handleShowConfirmation = this.handleShowConfirmation.bind(this)
    window.addEventListener('show-confirmation', this.handleShowConfirmation)
  }

  disconnect() {
    window.removeEventListener('show-confirmation', this.handleShowConfirmation)
  }

  handleShowConfirmation(event) {
    this.showConfirmationWindow(event.detail)
  }

  showConfirmationWindow({
    title = '',
    message = '',
    confirmLabel = '',
    cancelLabel = '',
    onConfirm,
    onCancel
  } = {}) {

    this.dialog.setAttribute('title', title)
    this.messageTarget.innerHTML = message
    this.confirmLabelTarget.textContent = confirmLabel
    this.cancelLabelTarget.textContent = cancelLabel

    this.onConfirmCallback = onConfirm
    this.onCancelCallback = onCancel

    this.dialog.showModal()
    document.body.style.overflow = 'hidden'
  }

  #close() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.dispatchEvent(new Event('ajax:complete'))
    }
    this.dialog.close()
    document.body.style.overflow = ''
  }

  async confirm() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.dispatchEvent(new Event('ajax:send'))
    }
    if (typeof this.onConfirmCallback === 'function') await this.onConfirmCallback()
    this.#close()
  }

  cancel() {
    this.#close()
    if (typeof this.onCancelCallback === 'function') this.onCancelCallback()
  }
}